import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Search, X, Phone, Mail, ChevronDown, ChevronUp, User, Eye,
  Award, FileText, Upload, Send, Calendar, MapPin, Clock, CheckCircle, XCircle,
  AlertCircle, Download, Camera, Briefcase, CreditCard, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { openBlobInNewTab, base64ToBlob } from '../utils/fileUtils';

const Evaluateurs = ({ user }) => {
  const [evaluateurs, setEvaluateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // Convocations
  const [convocations, setConvocations] = useState([]);
  const [showConvocationForm, setShowConvocationForm] = useState(false);
  const [evaluateurUsers, setEvaluateurUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [convForm, setConvForm] = useState({
    evaluateur_user_id: '', date_epreuve: '', heure: '', lieu: '', type_epreuve: 'EPMSP', notes: ''
  });

  // Upload documents
  const [showUploadModal, setShowUploadModal] = useState(null);
  const [uploadData, setUploadData] = useState({ type: 'carte_pro', description: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Add evaluateur form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ nom: '', prenom: '', email: '', telephone: '', diplomes: '', experience: '', numero_carte_pro: '', date_expiration_carte: '' });

  useEffect(() => { loadEvaluateurs(); loadConvocations(); loadEvaluateurUsers(); }, []);

  const loadEvaluateurs = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/evaluateurs');
      // Enrich with documents
      const enriched = await Promise.all((res.data || []).map(async (ev) => {
        try {
          const docsRes = await axios.get(`/documents/${ev.user_id || ev.evaluateur_id}`).catch(() => ({ data: [] }));
          return { ...ev, documents: docsRes.data || [] };
        } catch { return { ...ev, documents: [] }; }
      }));
      setEvaluateurs(enriched);
    } catch { toast.error('Erreur chargement evaluateurs'); }
    finally { setLoading(false); }
  };

  const loadConvocations = async () => {
    try {
      const res = await axios.get('/convocations');
      setConvocations(res.data);
    } catch { /* ignore */ }
  };

  const loadEvaluateurUsers = async () => {
    try {
      const res = await axios.get('/evaluateurs-users');
      setEvaluateurUsers(res.data);
    } catch { /* ignore */ }
  };

  const handleSendConvocation = async () => {
    if (!convForm.evaluateur_user_id || !convForm.date_epreuve) {
      toast.error('Selectionnez un evaluateur et une date');
      return;
    }
    try {
      setSaving(true);
      await axios.post('/convocations', convForm);
      toast.success('Convocation envoyee');
      setShowConvocationForm(false);
      setConvForm({ evaluateur_user_id: '', date_epreuve: '', heure: '', lieu: '', type_epreuve: 'EPMSP', notes: '' });
      loadConvocations();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur envoi convocation'); }
    finally { setSaving(false); }
  };

  const handleDeleteConvocation = async (id) => {
    if (!window.confirm('Supprimer cette convocation ?')) return;
    try {
      await axios.delete(`/convocations/${id}`);
      toast.success('Convocation supprimee');
      loadConvocations();
    } catch { toast.error('Erreur suppression'); }
  };

  const handleAddEvaluateur = async () => {
    if (!addForm.nom || !addForm.prenom) { toast.error('Nom et prenom requis'); return; }
    try {
      setSaving(true);
      await axios.post('/evaluateurs', addForm);
      toast.success('Evaluateur ajoute');
      setShowAddForm(false);
      setAddForm({ nom: '', prenom: '', email: '', telephone: '', diplomes: '', experience: '', numero_carte_pro: '', date_expiration_carte: '' });
      loadEvaluateurs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDeleteEvaluateur = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} ?`)) return;
    try {
      await axios.delete(`/evaluateurs/${id}`);
      toast.success('Evaluateur supprime');
      loadEvaluateurs();
    } catch { toast.error('Erreur suppression'); }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) { toast.error('Selectionnez un fichier'); return; }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('user_id', showUploadModal);
      formData.append('document_type', uploadData.type);
      await axios.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploade');
      setShowUploadModal(null);
      setUploadData({ type: 'carte_pro', description: '' });
      setSelectedFile(null);
      loadEvaluateurs();
    } catch { toast.error('Erreur upload'); setUploading(false); }
    finally { setUploading(false); }
  };

  const viewDocument = async (docId) => {
    try {
      const res = await axios.get(`/documents/download/${docId}`);
      if (res.data?.content_base64) {
        openBlobInNewTab(base64ToBlob(res.data.content_base64, res.data.mime_type || 'application/pdf'));
      }
    } catch { toast.error('Erreur ouverture document'); }
  };

  const deleteDocument = async (docId, userId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`/documents/${docId}`);
      toast.success('Document supprime');
      loadEvaluateurs();
    } catch { toast.error('Erreur suppression'); }
  };

  const getConvStatutBadge = (statut) => {
    switch (statut) {
      case 'envoyee': return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">En attente</Badge>;
      case 'acceptee': return <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Acceptee</Badge>;
      case 'refusee': return <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">Refusee</Badge>;
      default: return <Badge variant="outline" className="text-xs">{statut}</Badge>;
    }
  };

  const filtered = evaluateurs.filter(ev =>
    `${ev.prenom} ${ev.nom} ${ev.email}`.toLowerCase().includes(search.toLowerCase())
  );

  // Map convocations per evaluateur
  const getConvForEval = (userId) => convocations.filter(c => c.evaluateur_user_id === userId);

  if (loading) return (
    <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div></div>
  );

  return (
    <div className="space-y-6" data-testid="evaluateurs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Evaluateurs</h1>
          <p className="text-sm text-slate-500">Profils complets, documents et convocations EPMSP</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowConvocationForm(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-convocation-btn">
            <Send className="h-4 w-4 mr-1" /> Convoquer
          </Button>
          <Button onClick={() => setShowAddForm(true)} className="bg-[#8DC63F] hover:bg-[#7db336] text-white" data-testid="add-evaluateur-btn">
            <Plus className="h-4 w-4 mr-1" /> Ajouter
          </Button>
        </div>
      </div>

      {/* Stats + Search */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 bg-gradient-to-r from-[#8DC63F]/10 to-transparent p-4 rounded-xl border border-[#8DC63F]/20 flex-1">
          <div className="w-12 h-12 bg-[#8DC63F]/20 rounded-full flex items-center justify-center">
            <User className="h-6 w-6 text-[#8DC63F]" />
          </div>
          <div>
            <span className="text-2xl font-bold text-[#0F172A]">{evaluateurs.length}</span>
            <p className="text-sm text-slate-500">Evaluateurs enregistres</p>
          </div>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-evaluateurs"
          />
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {filtered.map((ev) => {
          const uid = ev.user_id || ev.evaluateur_id;
          const isExpanded = expandedId === uid;
          const nom = ev.nom || '';
          const prenom = ev.prenom || '';
          const fullName = `${prenom} ${nom}`.trim() || 'Evaluateur';
          const docs = ev.documents || [];
          const cartePro = docs.filter(d => (d.document_type || d.type || '').toLowerCase().includes('carte'));
          const diplomes = docs.filter(d => (d.document_type || d.type || '').toLowerCase().includes('diplome'));
          const photoId = docs.filter(d => (d.document_type || d.type || '').toLowerCase().includes('photo'));
          const autresDocs = docs.filter(d => !['carte', 'diplome', 'photo'].some(k => (d.document_type || d.type || '').toLowerCase().includes(k)));
          const evalConvocations = getConvForEval(uid);

          return (
            <div key={uid} className="bg-white border border-slate-200 rounded-xl hover:shadow-lg transition-all" data-testid={`evaluateur-${uid}`}>
              {/* Header row */}
              <div
                className="p-4 cursor-pointer hover:bg-slate-50"
                onClick={() => setExpandedId(isExpanded ? null : uid)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-[4.5rem] rounded-md overflow-hidden bg-gradient-to-br from-[#8DC63F] to-green-700 flex items-center justify-center text-white text-xl font-bold">
                      {(ev.photo_identite || ev.photo) ? (
                        <img src={ev.photo_identite || ev.photo} alt={fullName} className="w-full h-full object-cover" />
                      ) : (
                        fullName.split(' ').map(n => n?.[0] || '').join('').substring(0, 2).toUpperCase()
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-[#0F172A]">{fullName}</h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        {ev.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {ev.email}</span>}
                        {ev.telephone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {ev.telephone}</span>}
                        {ev.numero_carte_pro && <span className="flex items-center gap-1"><CreditCard className="h-3 w-3" /> {ev.numero_carte_pro}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {evalConvocations.length > 0 && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        {evalConvocations.length} convocation(s)
                      </span>
                    )}
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                      {cartePro.length} carte(s)
                    </span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      {diplomes.length} diplome(s)
                    </span>
                    <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                      {docs.length} doc(s)
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteEvaluateur(uid, fullName); }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                  </div>
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="border-t border-slate-200 bg-gradient-to-b from-green-50 to-white p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Col 1: Profil */}
                    <div className="bg-white p-5 rounded-xl border border-green-200 shadow-sm">
                      <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                        <User className="h-5 w-5" /> Profil
                      </h4>
                      <div className="flex justify-center mb-4">
                        <div className="w-20 h-28 rounded-md overflow-hidden bg-green-100 border-4 border-green-200">
                          {(ev.photo_identite || ev.photo) ? (
                            <img src={ev.photo_identite || ev.photo} alt={fullName} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-green-400"><Camera className="h-8 w-8" /></div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        {[
                          ['Nom', nom], ['Prenom', prenom],
                          ['Email', ev.email], ['Telephone', ev.telephone],
                          ['N. carte pro', ev.numero_carte_pro],
                          ['Expiration carte', ev.date_expiration_carte],
                          ['Diplomes', ev.diplomes],
                          ['Experience', ev.experience],
                          ['Centre formation', ev.centre_formation],
                        ].map(([label, val]) => val ? (
                          <div key={label} className="flex justify-between py-1.5 border-b border-slate-100">
                            <span className="text-slate-500">{label}</span>
                            <span className="font-medium text-right max-w-[60%]">{val}</span>
                          </div>
                        ) : null)}
                      </div>
                      {ev.signature && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-sm text-slate-500 mb-2">Signature</p>
                          <img src={ev.signature} alt="Signature" className="max-h-16 border border-slate-200 rounded" />
                        </div>
                      )}
                    </div>

                    {/* Col 2: Documents (carte pro, diplomes, photo, autres) */}
                    <div className="bg-white p-5 rounded-xl border border-green-200 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-green-800 flex items-center gap-2">
                          <Award className="h-5 w-5" /> Documents ({docs.length})
                        </h4>
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); setShowUploadModal(uid); }} className="bg-[#8DC63F] hover:bg-[#7db336] h-7 text-xs">
                          <Upload className="h-3 w-3 mr-1" /> Ajouter
                        </Button>
                      </div>

                      {/* Carte pro */}
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Carte Professionnelle</p>
                      {cartePro.length === 0 ? (
                        <div className="text-center py-3 bg-slate-50 rounded-lg mb-3">
                          <CreditCard className="h-5 w-5 text-slate-300 mx-auto mb-1" />
                          <p className="text-slate-400 text-xs">Non uploade</p>
                        </div>
                      ) : cartePro.map((d, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg border border-amber-100 mb-2">
                          <div className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-amber-600" /><span className="text-xs font-medium truncate">{d.filename || d.document_type || 'Carte Pro'}</span></div>
                          <div className="flex gap-1">
                            <button onClick={() => viewDocument(d.document_id)} className="p-1.5 text-blue-500 hover:bg-blue-100 rounded"><Eye className="h-3 w-3" /></button>
                            <button onClick={() => deleteDocument(d.document_id, uid)} className="p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 className="h-3 w-3" /></button>
                          </div>
                        </div>
                      ))}

                      {/* Diplomes */}
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2 mt-3">Diplomes</p>
                      {diplomes.length === 0 ? (
                        <div className="text-center py-3 bg-slate-50 rounded-lg mb-3">
                          <Award className="h-5 w-5 text-slate-300 mx-auto mb-1" />
                          <p className="text-slate-400 text-xs">Non uploade</p>
                        </div>
                      ) : diplomes.map((d, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-100 mb-2">
                          <div className="flex items-center gap-2"><Award className="h-4 w-4 text-green-600" /><span className="text-xs font-medium truncate">{d.filename || d.document_type || 'Diplome'}</span></div>
                          <div className="flex gap-1">
                            <button onClick={() => viewDocument(d.document_id)} className="p-1.5 text-blue-500 hover:bg-blue-100 rounded"><Eye className="h-3 w-3" /></button>
                            <button onClick={() => deleteDocument(d.document_id, uid)} className="p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 className="h-3 w-3" /></button>
                          </div>
                        </div>
                      ))}

                      {/* Photo d'identite */}
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2 mt-3">Photo d'Identite</p>
                      {photoId.length === 0 ? (
                        <div className="text-center py-3 bg-slate-50 rounded-lg mb-3">
                          <Camera className="h-5 w-5 text-slate-300 mx-auto mb-1" />
                          <p className="text-slate-400 text-xs">Non uploade</p>
                        </div>
                      ) : photoId.map((d, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-100 mb-2">
                          <div className="flex items-center gap-2"><Camera className="h-4 w-4 text-blue-600" /><span className="text-xs font-medium truncate">{d.filename || 'Photo identite'}</span></div>
                          <div className="flex gap-1">
                            <button onClick={() => viewDocument(d.document_id)} className="p-1.5 text-blue-500 hover:bg-blue-100 rounded"><Eye className="h-3 w-3" /></button>
                            <button onClick={() => deleteDocument(d.document_id, uid)} className="p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 className="h-3 w-3" /></button>
                          </div>
                        </div>
                      ))}

                      {/* Autres */}
                      {autresDocs.length > 0 && (
                        <>
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-2 mt-3">Autres</p>
                          {autresDocs.map((d, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-200 mb-2">
                              <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-slate-600" /><span className="text-xs font-medium truncate">{d.filename || d.document_type || 'Document'}</span></div>
                              <div className="flex gap-1">
                                <button onClick={() => viewDocument(d.document_id)} className="p-1.5 text-blue-500 hover:bg-blue-100 rounded"><Eye className="h-3 w-3" /></button>
                                <button onClick={() => deleteDocument(d.document_id, uid)} className="p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 className="h-3 w-3" /></button>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>

                    {/* Col 3: Convocations */}
                    <div className="bg-white p-5 rounded-xl border border-blue-200 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                          <Calendar className="h-5 w-5" /> Convocations ({evalConvocations.length})
                        </h4>
                        <Button size="sm" onClick={(e) => {
                          e.stopPropagation();
                          setConvForm(f => ({ ...f, evaluateur_user_id: uid }));
                          setShowConvocationForm(true);
                        }} className="bg-blue-600 hover:bg-blue-700 h-7 text-xs text-white">
                          <Send className="h-3 w-3 mr-1" /> Convoquer
                        </Button>
                      </div>
                      {evalConvocations.length === 0 ? (
                        <div className="text-center py-8 bg-slate-50 rounded-lg">
                          <Calendar className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-400 text-sm">Aucune convocation</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {evalConvocations.map(c => (
                            <div key={c.convocation_id} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-blue-800">{c.type_epreuve || 'EPMSP'}</span>
                                {getConvStatutBadge(c.statut)}
                              </div>
                              <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{c.date_epreuve ? new Date(c.date_epreuve).toLocaleDateString('fr-FR') : '-'}</span>
                                {c.heure && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{c.heure}</span>}
                                {c.lieu && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{c.lieu}</span>}
                              </div>
                              {c.notes && <p className="text-xs text-slate-500 italic mt-1">{c.notes}</p>}
                              <div className="flex justify-end mt-2">
                                <button onClick={(e) => { e.stopPropagation(); handleDeleteConvocation(c.convocation_id); }}
                                  className="p-1 text-red-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <User className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">{search ? 'Aucun evaluateur trouve' : 'Aucun evaluateur enregistre'}</p>
          </div>
        )}
      </div>

      {/* Convocation Form Modal */}
      {showConvocationForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="convocation-form-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Nouvelle convocation</h3>
              <button onClick={() => setShowConvocationForm(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Evaluateur *</label>
                <select
                  value={convForm.evaluateur_user_id}
                  onChange={e => setConvForm(f => ({ ...f, evaluateur_user_id: e.target.value }))}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  data-testid="select-evaluateur"
                >
                  <option value="">Selectionnez un evaluateur</option>
                  {evaluateurUsers.map(u => (
                    <option key={u.user_id} value={u.user_id}>{u.prenom} {u.nom} ({u.email})</option>
                  ))}
                  {/* Also show evaluateurs from evaluateurs collection */}
                  {evaluateurs.filter(ev => !evaluateurUsers.find(u => u.user_id === ev.user_id)).map(ev => (
                    <option key={ev.evaluateur_id} value={ev.user_id || ev.evaluateur_id}>{ev.prenom} {ev.nom} ({ev.email})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Date *</label>
                  <Input type="date" value={convForm.date_epreuve} onChange={e => setConvForm(f => ({ ...f, date_epreuve: e.target.value }))} data-testid="input-date-epreuve" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Heure</label>
                  <Input type="time" value={convForm.heure} onChange={e => setConvForm(f => ({ ...f, heure: e.target.value }))} data-testid="input-heure" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Lieu</label>
                <Input value={convForm.lieu} onChange={e => setConvForm(f => ({ ...f, lieu: e.target.value }))} placeholder="Lieu de l'evaluation" data-testid="input-lieu" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Notes</label>
                <textarea value={convForm.notes} onChange={e => setConvForm(f => ({ ...f, notes: e.target.value }))} placeholder="Informations complementaires..." className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none" rows={2} data-testid="input-notes" />
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowConvocationForm(false)}>Annuler</Button>
              <Button onClick={handleSendConvocation} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-send-btn">
                {saving ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="upload-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Ajouter un document</h3>
              <button onClick={() => setShowUploadModal(null)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Type de document</label>
                <select value={uploadData.type} onChange={e => setUploadData(d => ({ ...d, type: e.target.value }))}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
                  <option value="carte_pro">Carte Professionnelle a jour</option>
                  <option value="diplome">Diplome</option>
                  <option value="photo_identite">Photo d'Identite</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Description / Competences</label>
                <textarea value={uploadData.description} onChange={e => setUploadData(d => ({ ...d, description: e.target.value }))}
                  placeholder="Description du document ou competences..." className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none" rows={2} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Fichier</label>
                <input type="file" ref={fileInputRef} onChange={e => setSelectedFile(e.target.files[0])} className="w-full text-sm" accept=".pdf,.jpg,.jpeg,.png" />
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowUploadModal(null)}>Annuler</Button>
              <Button onClick={handleUploadDocument} disabled={uploading} className="bg-[#8DC63F] hover:bg-[#7db336] text-white">
                {uploading ? 'Upload...' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Evaluateur Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="add-evaluateur-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Ajouter un evaluateur</h3>
              <button onClick={() => setShowAddForm(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Prenom *</label>
                  <Input value={addForm.prenom} onChange={e => setAddForm(f => ({ ...f, prenom: e.target.value }))} placeholder="Prenom" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Nom *</label>
                  <Input value={addForm.nom} onChange={e => setAddForm(f => ({ ...f, nom: e.target.value }))} placeholder="Nom" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Email</label>
                  <Input type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Telephone</label>
                  <Input value={addForm.telephone} onChange={e => setAddForm(f => ({ ...f, telephone: e.target.value }))} placeholder="Telephone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">N. Carte Pro</label>
                  <Input value={addForm.numero_carte_pro} onChange={e => setAddForm(f => ({ ...f, numero_carte_pro: e.target.value }))} placeholder="Numero carte pro" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Expiration carte</label>
                  <Input type="date" value={addForm.date_expiration_carte} onChange={e => setAddForm(f => ({ ...f, date_expiration_carte: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Diplomes / Competences</label>
                <textarea value={addForm.diplomes} onChange={e => setAddForm(f => ({ ...f, diplomes: e.target.value }))} placeholder="Diplomes et competences..." className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none" rows={2} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Experience</label>
                <textarea value={addForm.experience} onChange={e => setAddForm(f => ({ ...f, experience: e.target.value }))} placeholder="Experience professionnelle..." className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none" rows={2} />
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Annuler</Button>
              <Button onClick={handleAddEvaluateur} disabled={saving} className="bg-[#8DC63F] hover:bg-[#7db336] text-white">
                {saving ? 'Ajout...' : 'Ajouter'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Evaluateurs;
